import type {
  BaseTheoryQuestion,
  FreeTextTheoryQuestion,
  ImageChoiceOption,
  ImageChoiceTheoryQuestion,
  MultipleChoiceOption,
  MultipleChoiceTheoryQuestion,
  TheoryQuestion,
} from "./TheoryQuestion";
import { downloadFile } from "../utils/downloadFile";
import { z } from "zod";

export interface TheoryQuestionsCatalog {
  /** Composite id in the format "{version}:{language}", e.g. "2024-Oktober:de" */
  readonly id: string;
  readonly version: string;
  readonly language: string;
  readonly questionsById: { [id: string]: TheoryQuestion };
}

const DEFAULT_VERSION = "2024-Oktober";

const _cache: Map</* version: */ string, Promise<TheoryQuestionsCatalog>> = new Map();

export async function getTheoryQuestionsCatalog(version?: string): Promise<TheoryQuestionsCatalog> {
  version ??= DEFAULT_VERSION;
  let cacheEntry = _cache.get(version);
  if (!cacheEntry) {
    cacheEntry = _fetchTheoryQuestionsCatalog(version);
    _cache.set(version, cacheEntry);
  }
  try {
    const catalog = await cacheEntry;
    return catalog;
  } catch (error) {
    _cache.delete(version);
    throw error;
  }
}

async function _fetchTheoryQuestionsCatalog(version: string): Promise<TheoryQuestionsCatalog> {
  const json = await downloadFile(`https://storage.googleapis.com/autovio-cdn/theory_v4/${version}/de.json`);
  const { questions } = TheoryQuestionsCatalogSchema.parse(JSON.parse(json));
  const questionsById: { [id: string]: TheoryQuestion } = {};
  for (const question of questions) {
    questionsById[question.id] = question;
  }
  const catalog: TheoryQuestionsCatalog = {
    id: `${version}:de`,
    version,
    language: "de",
    questionsById,
  };
  return catalog;
}

const BaseQuestionFieldsSchema = z.object({
  id: z.string().describe("the official question id"),
  t: z.string().describe("title"),
  s: z.optional(z.string()).describe("subtitle"),
  c: z.optional(z.string()).describe("image caption"),
  jpg: z.optional(z.tuple([z.number().int().gt(0), z.number().int().gt(0)])).describe(`
    If present:
    - the size of the small image (640x384, https://img.autovio.de/fragenkatalog/{questionId}.jpg) in bytes,
    - the size of the large image (https://img.autovio.de/fragenkatalog/{questionId}-1280x768.jpg) in bytes
  `),
  png: z.optional(z.number().int().gt(0)).describe(`
    If present: the size of the image file (2400x1440, https://img.autovio.de/fragenkatalog/{questionId}.png) in bytes
  `),
  mp4: z.optional(
    z.tuple([
      z.number().int().gt(0),
      z.number().int().gt(0),
      z.number().int().gt(0),
      z.number().int().gt(0),
      z.number().int().gt(0),
      z.number().int().gt(0),
    ]),
  ).describe(`
    If present:
    - the size of the first frame (640x384, https://img.autovio.de/fragenkatalog/{questionId}_anfang.jpg) in bytes
    - the size of the video (640x384, https://img.autovio.de/fragenkatalog/{questionId}.mp4)) in bytes
    - the size of the last frame (640x384, https://img.autovio.de/fragenkatalog/{questionId}_ende.jpg) in bytes
    - the size of the large first frame (https://img.autovio.de/fragenkatalog/{questionId}_anfang-1280x768.jpg) in bytes
    - the size of the large video (https://img.autovio.de/fragenkatalog/{questionId}-1280x768.mp4)) in bytes
    - the size of the large last frame (https://img.autovio.de/fragenkatalog/{questionId}_ende-1280x768.jpg) in bytes
  `),
  p: z.number().int().describe("points"),
});

const MultipleChoiceOptionSchema = z.tuple([
  z.string().describe("option text"),
  z.union([z.literal(0), z.literal(1)]).describe("0: incorrect answer, 1: correct answer"),
]);

const MultipleChoiceQuestionSchema = BaseQuestionFieldsSchema.extend({
  o: z.array(MultipleChoiceOptionSchema).min(2).max(3).describe("the options of the multiple choice question"),
}).transform(
  (input) =>
    ({
      ..._baseQuestionFields(input),
      typ: "Multiple-Choice-Frage",
      options: input.o.map(_multipleChoiceOption),
    }) satisfies MultipleChoiceTheoryQuestion,
);

const FreeTextQuestionSchema = BaseQuestionFieldsSchema.extend({
  x: z.string().describe('text with the placeholder(s) " ___ " where input fields should be rendered'),
  y: z.union([z.string(), z.array(z.string()).length(2)]).describe("the correct answer(s)"),
}).transform(
  (val) =>
    ({
      ..._baseQuestionFields(val),
      typ: "Zahlenfrage",
      textWithPlaceholders: val.x,
      correctAnswers: (typeof val.y === "string" ? [val.y] : val.y).map((ans) => _parseGermanNumber(ans)),
    }) satisfies FreeTextTheoryQuestion,
);

const ImageChoiceOptionSchema = z.tuple([
  z
    .number()
    .int()
    .gt(0)
    .describe("the size of the image file (https://img.autovio.de/fragenkatalog/{questionId}_a{1|2|3}.png) in bytes"),
  z.number().int().gt(0).describe("the width of the image in pixels"),
  z.number().int().gt(0).describe("the height of the image in pixels"),
  z.union([z.literal(0), z.literal(1)]).describe("0: incorrect answer, 1: correct answer"),
]);

const ImageChoiceQuestionSchema = BaseQuestionFieldsSchema.extend({
  i: z.array(ImageChoiceOptionSchema).min(2).max(3).describe("the options of the image choice question"),
}).transform(
  (input) =>
    ({
      ..._baseQuestionFields(input),
      typ: "Bildauswahlfrage",
      options: input.i.map((o, index) =>
        _imageChoiceOption(`https://img.autovio.de/fragenkatalog/${input.id}_a${index + 1}.png`, o),
      ),
    }) satisfies ImageChoiceTheoryQuestion,
);

const TheoryQuestionSchema = z.union([MultipleChoiceQuestionSchema, FreeTextQuestionSchema, ImageChoiceQuestionSchema]);

/**
 * Schema of the theory questions catalog json files stored here:
 * https://console.cloud.google.com/storage/browser/autovio-cdn/theory_v4
 */
const TheoryQuestionsCatalogSchema = z.object({
  meta: z.object({ version: z.string(), lang: z.string() }),
  questions: z.array(TheoryQuestionSchema),
});

function _baseQuestionFields(input: z.input<typeof BaseQuestionFieldsSchema>): BaseTheoryQuestion {
  const { id, jpg, png, mp4 } = input;
  const baseFields: BaseTheoryQuestion = {
    id,
    title: _removeSquareBrackets(input.t),
    subtitle: _removeSquareBrackets(input.s || ""),
    ...(input.c ? { imageCaption: input.c } : {}),
    points: typeof input.p === "number" ? input.p : 0,
  };
  if (jpg) {
    baseFields.image = { url: `https://img.autovio.de/fragenkatalog/${id}-1280x768.jpg` };
  } else if (png !== undefined) {
    baseFields.image = { url: `https://img.autovio.de/fragenkatalog/${id}.png` };
  }
  if (mp4) {
    baseFields.video = {
      firstFrame: { url: `https://img.autovio.de/fragenkatalog/${id}_anfang-1280x768.jpg` },
      lastFrame: { url: `https://img.autovio.de/fragenkatalog/${id}_ende-1280x768.jpg` },
      url: `https://img.autovio.de/fragenkatalog/${id}-1280x768.mp4`,
    };
  }
  return baseFields;
}

function _removeSquareBrackets(s: string): string {
  return s.replace(/\[/g, "").replace(/]/g, "");
}

function _parseGermanNumber(s: string): number {
  const normalized = s.replace(/\./g, "").replace(",", ".");
  return parseFloat(normalized);
}

function _multipleChoiceOption(input: z.input<typeof MultipleChoiceOptionSchema>): MultipleChoiceOption {
  const text = _removeSquareBrackets(input[0]).replace(/^-\s*/, "");
  const isCorrect = input[1] === 1;
  return { text, isCorrect };
}

function _imageChoiceOption(imageUrl: string, input: z.input<typeof ImageChoiceOptionSchema>): ImageChoiceOption {
  return {
    image: { url: imageUrl },
    imageWidth: input[1],
    imageHeight: input[2],
    isCorrect: input[3] === 1,
  };
}
